import { isPlatform } from '@ionic/react';
import axios, { AxiosInstance } from 'axios';
import axiosRetry from 'axios-retry';
import {
  ChecklistEntryUpdate,
  WP_Checklist,
  WP_ChecklistEntry,
} from '../model/checklist/checklist';
import { GFormsSubmitData, GF_FormSubmitResult } from '../model/gforms/gform';
import { WP_Place } from '../model/map/map';
import type {
  EntryUpdate,
  HouseArray,
  HousePageArray,
  NotifUpdate,
  SearchParams,
  ServiceArray,
  User,
  UserNetworkUpdate,
  UserUpdate,
  WP_DirUser,
  WP_Entry,
  WP_EventsArray,
  WP_News,
  WP_NewsArray,
  WP_NotifsArray,
  WP_Page,
  WP_Pagination,
  WP_PaginationResult,
  WP_Search_Result,
  WP_ServiceTerm,
  WP_UserNetwork,
} from '../model/models';
import { WP_Tag } from '../model/tags/tags';
import { Auth } from './AuthService';
import { ActionType } from '../state/GlobalState';

const https = require('https');

const querystring = require('querystring');

export type WP_FetchPage<T> = (
  pagination: WP_Pagination,
  ...args: any[]
) => Promise<WP_PaginationResult<T>>;

export class WPService {
  private static instance: WPService | undefined;

  private logger = console;

  private auth;

  private axiosInstance: AxiosInstance = axios.create({
    baseURL: process.env.REACT_APP_WP_URL + '/wp-json', //for local testing in IOS, we have to use https://admin.portaildev.ciup.fr instead of process.env.REACT_APP_WP_URL
    timeout: 30000,
    httpsAgent: new https.Agent({ keepAlive: true }),
    transitional: {
      silentJSONParsing: false,
      forcedJSONParsing: true,
      clarifyTimeoutError: true,
    },
    headers: {
      'Content-Type': 'application/json; charset=UTF-8',
    },
  });

  private axiosInstanceAnonymous: AxiosInstance = axios.create({
    baseURL: process.env.REACT_APP_WP_URL + '/wp-json', //for local testing in IOS, we have to use https://admin.portaildev.ciup.fr instead of process.env.REACT_APP_WP_URL
    timeout: 30000,
    httpsAgent: new https.Agent({ keepAlive: true }),
    transitional: {
      silentJSONParsing: false,
      forcedJSONParsing: true,
      clarifyTimeoutError: true,
    },
    headers: {
      'Content-Type': 'application/json; charset=UTF-8',
    },
  });

  public static getInstance(logger, auth): WPService {
    if (!this.instance) {
      this.instance = new WPService();
      this.instance.init(logger, auth);
    }

    return this.instance;
  }

  public getAxiosInstance(): AxiosInstance {
    return this.axiosInstance;
  }

  private init(logger, auth): void {
    const self = this;

    self.logger = logger;
    self.auth = auth;
  }

  public initAxiosInterceptors(state, dispatch) {
    const self = this;

    // Add a request interceptor
    this.axiosInstance.interceptors.request.use(
      async function (config) {
        const wpToken = await Auth.getWpToken();
        const accessToken = wpToken ? wpToken : self.getAccessToken();
        const idToken = self.getIdToken();
        // Add auth. headers
        config.headers['Authorization'] = `Bearer ${accessToken}`;
        config.headers['X-Identity'] = `Token ${idToken}`;
        self.logger.debug(
          `Axios request ${config.method} ${config.url}`,
          config
        );
        return config;
      },
      function (error) {
        // Do something with request error
        self.logger.warn('Axios interceptor request', error);
        return Promise.reject(error);
      }
    );

    // Add a response interceptor
    this.axiosInstance.interceptors.response.use(
      async function (res) {
        // Any status code that lie within the range of 2xx cause this function to trigger
        // Do something with response data
        self.logger.debug(`Axios response`, res);
        if (res.headers['x-wp-token']) {
          await Auth.setWpToken(res.headers['x-wp-token']);
        }
        return res;
      },
      async function (err) {
        // Any status codes that falls outside the range of 2xx cause this function to trigger
        // Do something with response error
        self.logger.warn(
          'Axios interceptor response: ' + err.response?.data?.code,
          err
        );
        // if 403
        if (err.response && err.response.status === 403) {
          const statusCode = err.response?.data?.code;
          if (
            statusCode === 'jwt_auth_invalid_token' ||
            statusCode === 'jwt_auth_no_auth_header'
          ) {
            await Auth.signOut();

            dispatch({
              type: ActionType.Disconnect,
              payload: false,
            });

            if (isPlatform('capacitor')) {
              self.auth.endSessionCallback();
              window.location.reload();
            }
          }
        }
        return Promise.reject(err);
      }
    );
    axiosRetry(this.axiosInstance, {
      retries: 3,
      retryDelay: axiosRetry.exponentialDelay,
    });
  }

  public getAccessToken(): string | undefined {
    return this.auth.session.token?.accessToken;
  }

  public getIdToken(): string | undefined {
    return this.auth.session.token?.idToken;
  }

  private fetch(resource: string, params?: any) {
    const stringParams = params ? '?' + querystring.stringify(params) : '';
    return this.axiosInstance.get(resource + stringParams);
  }

  private fetchPage<T>(
    resource: string,
    pagination: WP_Pagination,
    params?: any
  ): Promise<WP_PaginationResult<T>> {
    const mergedParams = { ...params, ...pagination };
    const stringParams = mergedParams
      ? '?' + querystring.stringify(mergedParams)
      : '';
    return new Promise<WP_PaginationResult<T>>((resolve, reject) => {
      this.axiosInstance.get(resource + stringParams).then(
        (res) => {
          resolve({
            items: res.data,
            total: +res.headers['x-wp-total'],
            totalPages: +res.headers['x-wp-totalpages'],
          } as WP_PaginationResult<T>);
        },
        (reason) => {
          reject(reason);
        }
      );
    });
  }

  private post(resource: string, params?: any, body?: any) {
    const stringParams = params ? '?' + querystring.stringify(params) : '';
    return this.axiosInstance.post(resource + stringParams, body);
  }

  private postAnonymous(resource: string, params?: any, body?: any) {
    const stringParams = params ? '?' + querystring.stringify(params) : '';
    return this.axiosInstanceAnonymous.post(resource + stringParams, body);
  }

  private put(resource: string, params?: any, body?: any) {
    const stringParams = params ? '?' + querystring.stringify(params) : '';
    return this.axiosInstance.put(resource + stringParams, body);
  }

  private delete(resource: string, params?: any) {
    const stringParams = params ? '?' + querystring.stringify(params) : '';
    return this.axiosInstance.delete(resource + stringParams);
  }

  private formatLocale(locale: string) {
    return locale.split('_')[0];
  }

  public getHouses(): Promise<HouseArray> {
    const params = { per_page: 100 };
    return new Promise<HouseArray>((resolve, reject) => {
      this.fetch('/wp/v2/tax-house', params).then((res) => {
        resolve(res.data as HouseArray);
      });
    });
  }

  public getServicesTerm(): Promise<WP_ServiceTerm[]> {
    const params = { per_page: 100 };
    return new Promise<WP_ServiceTerm[]>((resolve, reject) => {
      this.fetch('/wp/v2/tax-service', params).then((res) => {
        resolve(res.data as WP_ServiceTerm[]);
      });
    });
  }

  public getUserInformations(): Promise<User> {
    return new Promise<User>((resolve, reject) => {
      const params = { context: 'edit' };
      this.fetch('/wp/v2/users/me', params)
        .then((res) => {
          resolve(res.data as User);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  public getHousePages(
    houseId: number,
    locale: string
  ): Promise<HousePageArray> {
    const params = { wpml_language: this.formatLocale(locale), per_page: 100 };
    return new Promise<HousePageArray>((resolve, reject) => {
      this.fetch('/wp/v2/house', params).then((res) => {
        const pages = res.data.filter((h) => {
          return (
            h['tax-house'] &&
            Array.isArray(h['tax-house']) &&
            h['tax-house'].includes(houseId)
          );
        });
        resolve(pages as HousePageArray);
      });
    });
  }

  public putUserAcf(userId: number, data: UserUpdate) {
    return this.putAcf(userId, 'users', data);
  }

  public putNotifAcf(notifId: number, data: NotifUpdate) {
    return this.putAcf(notifId, 'notification', data);
  }

  public putUserNetworkAcf(userNetworkId: number, data: UserNetworkUpdate) {
    return this.putAcf(userNetworkId, 'usernetwork', data);
  }

  private putAcf<U, T>(id: number, type: string, data: U) {
    return new Promise<T>((resolve, reject) => {
      this.put(`/acf/v3/${type}/${id}`, null, data)
        .then((res) => {
          resolve(res.data);
        })
        .catch((e) => {
          reject(e);
        });
    });
  }

  public postUserNetwork(data: UserNetworkUpdate): Promise<WP_UserNetwork> {
    return new Promise<WP_UserNetwork>((resolve, reject) => {
      this.post('/wp/v2/usernetwork', null, data)
        .then((res) => {
          resolve(res.data as WP_UserNetwork);
        })
        .catch((e) => {
          reject(e);
        });
    });
  }

  public deleteUserNetwork(id: number) {
    return this.delete(`/wp/v2/usernetwork/${id}`);
  }

  public getNews(locale: string, slug?: string): Promise<WP_NewsArray> {
    return new Promise((resolve, reject) => {
      const params = {
        wpml_language: this.formatLocale(locale),
        per_page: 20,
      };
      if (slug) {
        params['slug'] = slug;
      }
      this.fetch('/wp/v2/posts', params).then((res) => {
        resolve(
          res.data.sort((a: WP_News, b: WP_News) => {
            if (a.sticky === true) {
              return -1;
            } else if (b.sticky === true) {
              return 1;
            } else {
              return 0;
            }
          }) as WP_NewsArray
        );
      });
    });
  }

  public getNewsByHouse(
    houseId: number,
    locale: string
  ): Promise<WP_NewsArray> {
    return new Promise((resolve, reject) => {
      this.getNews(locale).then((news) => {
        const houseNews = news.filter(
          (news) =>
            // filter by house id
            Array.isArray(news.acf.house) &&
            news.acf.house.map((house) => house.term_id).includes(houseId)
        );
        resolve(houseNews);
      });
    });
  }

  public getServices(locale: string): Promise<ServiceArray> {
    return new Promise((resolve, reject) => {
      const params = {
        wpml_language: this.formatLocale(locale),
        per_page: 100,
      };
      this.fetch('/wp/v2/service', params).then((res) => {
        resolve(res.data as ServiceArray);
      });
    });
  }

  public getPages(locale: string): Promise<WP_Page[]> {
    return new Promise((resolve, reject) => {
      const params = {
        wpml_language: this.formatLocale(locale),
        per_page: 100,
      };
      this.fetch('/wp/v2/pages', params).then((res) => {
        resolve(res.data as WP_Page[]);
      });
    });
  }

  public getEventsList(locale: string): Promise<WP_EventsArray> {
    return new Promise((resolve, reject) => {
      const params = {
        wpml_language: this.formatLocale(locale),
        per_page: 100,
        order: 'asc',
        orderby: 'meta_value',
        meta_key: 'start',
      };
      this.fetch('/wp/v2/event', params).then((res) => {
        resolve(res.data as WP_EventsArray);
      });
    });
  }

  public getEventsByHouse(
    houseId: number,
    locale: string
  ): Promise<WP_EventsArray> {
    return new Promise((resolve, reject) => {
      this.getEventsList(locale).then((events) => {
        const houseEvents = events.filter(
          (events) => events.acf.organizer === houseId
        );
        resolve(houseEvents);
      });
    });
  }

  private getNotifications(locale: string): Promise<WP_NotifsArray> {
    return new Promise((resolve, reject) => {
      const params = {
        wpml_language: this.formatLocale(locale),
        per_page: 100,
      };
      this.fetch('/wp/v2/notification', params).then((res) => {
        resolve(res.data as WP_NotifsArray);
      });
    });
  }

  public getNotifsByUser(
    userId: string,
    locale: string
  ): Promise<WP_NotifsArray> {
    return new Promise((resolve, reject) => {
      this.getNotifications(locale).then((notifs) => {
        const userNotifs = notifs.filter(
          (notif) => notif.acf.recipient === userId
        );
        resolve(userNotifs);
      });
    });
  }

  public getUsers(
    pagination: WP_Pagination,
    params?: any
  ): Promise<WP_PaginationResult<WP_DirUser>> {
    return this.fetchPage('/wp/v2/users', pagination, params);
  }

  public getUserNetwork(): Promise<WP_UserNetwork[]> {
    return new Promise((resolve, reject) => {
      const params = {
        per_page: 100,
      };
      this.fetch('/wp/v2/usernetwork', params).then((res) => {
        resolve(
          res.data.filter(
            (un: WP_UserNetwork) => un.acf.user_a && un.acf.user_b
          ) as WP_UserNetwork[]
        );
      });
    });
  }

  public getUser(id: number): Promise<WP_DirUser> {
    return new Promise((resolve, reject) => {
      this.fetch('/wp/v2/users/' + id).then((res) => {
        resolve(res.data as WP_DirUser);
      });
    });
  }

  public putEntryAcf(entryId: number, data: EntryUpdate) {
    return this.putAcf(entryId, 'entry', data);
  }

  public postEntry(locale: string, data: EntryUpdate): Promise<WP_Entry> {
    return new Promise<WP_Entry>((resolve, reject) => {
      const params = {
        wpml_language: this.formatLocale(locale),
      };
      this.post('/wp/v2/entry', params, data)
        .then((res) => {
          resolve(res.data as WP_Entry);
        })
        .catch((e) => {
          reject(e);
        });
    });
  }

  public getChecklists(locale: string): Promise<WP_Checklist[]> {
    return new Promise((resolve, reject) => {
      const params = {
        wpml_language: this.formatLocale(locale),
      };
      this.fetch('/wp/v2/checklist', params)
        .then((res) => {
          resolve(res.data as WP_Checklist[]);
        })
        .catch((e) => {
          reject(e);
        });
    });
  }

  public getChecklistEntry(
    locale: string,
    id: number
  ): Promise<WP_ChecklistEntry[]> {
    return new Promise((resolve, reject) => {
      const params = {
        wpml_language: this.formatLocale(locale),
        post_id: id,
      };
      this.fetch(`/wp/v2/checklist_entries`, params)
        .then((res) => {
          resolve(res.data as WP_ChecklistEntry[]);
        })
        .catch((e) => {
          reject(e);
        });
    });
  }

  public postChecklistEntry(
    locale: string,
    data: ChecklistEntryUpdate
  ): Promise<WP_ChecklistEntry> {
    return new Promise<WP_ChecklistEntry>((resolve, reject) => {
      const params = {
        wpml_language: this.formatLocale(locale),
      };
      this.post('/wp/v2/checklist_entries', params, data)
        .then((res) => {
          resolve(res.data as WP_ChecklistEntry);
        })
        .catch((e) => {
          reject(e);
        });
    });
  }

  public putChecklistEntryAcf(checklistId: number, data: ChecklistEntryUpdate) {
    return this.putAcf<ChecklistEntryUpdate, WP_ChecklistEntry>(
      checklistId,
      'checklist_entries',
      data
    );
  }

  public getTaxTags(slug: string) {
    return new Promise<Array<WP_Tag>>((resolve, reject) => {
      const params = {
        slug: slug,
        per_page: 100,
      };
      this.fetch('/wp/v2/tax-tag', params).then((res) => {
        resolve(res.data);
      });
    });
  }

  public getTaxChildrenTags(parent_id: number, taxonomyName: string) {
    return new Promise<Array<WP_Tag>>((resolve, reject) => {
      const params = {
        parent: parent_id,
        per_page: 100,
      };
      this.fetch('/wp/v2/' + taxonomyName, params).then((res) => {
        resolve(res.data);
      });
    });
  }

  public getPlacesChildrenTags(parent_id: number, locale: string) {
    return new Promise<Array<WP_Place>>((resolve, reject) => {
      const params = {
        wpml_language: this.formatLocale(locale),
        per_page: 100,
      };
      this.fetch('/wp/v2/places?places-categories=' + parent_id, params).then(
        (res) => {
          resolve(res.data);
        }
      );
    });
  }

  public searchUsers<WP_DirUser>(
    pagination: WP_Pagination,
    search: string,
    otherParams?: Object
  ): Promise<WP_PaginationResult<WP_DirUser>> {
    const params = { ...otherParams, search: search };
    const result = this.fetchPage<WP_DirUser>(
      '/wp/v2/users',
      pagination,
      params
    );
    // apply a filter on users to match search keyword as workarround
    // because contacts are systematically added to search results even if not matching criteria.
    return result.then((r) => {
      r.items = r.items.filter((u) =>
        (u as any).name.toLowerCase().includes(search.toLowerCase())
      );
      return r;
    });
  }

  public search<T>(
    pagination: WP_Pagination,
    locale: string,
    param: SearchParams,
    search: string,
    resultFilter?: (it: T) => boolean
  ) {
    const params = {
      wpml_language: this.formatLocale(locale),
      type: param.type,
      subtype: param.subtype,
      search: search,
      _embed: '',
    };
    return this.fetchPage<WP_Search_Result<T>>(
      '/wp/v2/search',
      pagination,
      params
    ).then((results) => ({
      ...results,
      items: results.items
        .map((it) => it._embedded.self[0] as T)
        .filter((it) => {
          return resultFilter ? resultFilter(it) : true;
        }),
    }));
  }

  public getParentTags(taxonomyName: string) {
    return new Promise<Array<WP_Tag>>((resolve, reject) => {
      const params = {
        parent: 0,
        per_page: 100,
      };
      this.fetch('/wp/v2/' + taxonomyName, params).then((res) => {
        resolve(res.data);
      });
    });
  }

  public getPlaces(locale) {
    return new Promise<Array<WP_Place>>((resolve, reject) => {
      const params = {
        wpml_language: this.formatLocale(locale),
        per_page: 100,
      };
      this.fetch('/wp/v2/places', params).then((res) => {
        resolve(res.data);
      });
    });
  }

  public submitGForm(
    locale: string,
    formId: number,
    data: GFormsSubmitData,
    anonymous?: false
  ): Promise<GF_FormSubmitResult> {
    return new Promise<GF_FormSubmitResult>((resolve, reject) => {
      const params = {
        wpml_language: this.formatLocale(locale),
      };
      const postFunc =
        anonymous && anonymous === true ? this.postAnonymous : this.post;
      postFunc
        .call(this, `/gf/v2/forms/${formId}/submissions`, params, data)
        .then(
          (res) => {
            resolve(res.data as GF_FormSubmitResult);
          },
          (res) => {
            if (res?.response?.status === 400 && res.response.data) {
              resolve(res.response.data as GF_FormSubmitResult);
            } else {
              reject(res);
            }
          }
        )
        .catch((e) => {
          reject(e);
        });
    });
  }
}
